import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2, Save, X } from "lucide-react";

const SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const emptyForm = { slug: "", name_zh: "", name_en: "", sort_order: 0, active: true };

export default function AdminCategories() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | { ...form, __isNew: true } | existing category
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/categories", { params: { include_inactive: true } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...emptyForm, __isNew: true });
  const openEdit = (c) => setEditing({ ...c });

  const submit = async (e) => {
    e.preventDefault();
    if (editing.__isNew && !SLUG_RE.test((editing.slug || "").trim())) {
      toast.error(zh ? "Slug 格式无效 (小写字母/数字/-/_)" : "Invalid slug format (lowercase a-z, 0-9, -, _)");
      return;
    }
    if (!editing.name_zh?.trim() || !editing.name_en?.trim()) {
      toast.error(zh ? "请填写中英文名称" : "Both zh and en names are required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        slug: (editing.slug || "").trim().toLowerCase(),
        name_zh: editing.name_zh.trim(),
        name_en: editing.name_en.trim(),
        sort_order: Number(editing.sort_order) || 0,
        active: !!editing.active,
      };
      if (editing.__isNew) {
        await api.post("/admin/categories", body);
      } else {
        await api.patch(`/admin/categories/${body.slug}`, body);
      }
      toast.success(t("common.saved"));
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const del = async (slug) => {
    if (!window.confirm(zh ? `确定删除分类「${slug}」?` : `Delete category "${slug}"?`)) return;
    try {
      await api.delete(`/admin/categories/${slug}`);
      toast.success(t("common.saved"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  return (
    <div className="p-6 md:p-10" data-testid="admin-categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">
            {zh ? "产品分类" : "Product Categories"}
          </h1>
          <p className="mt-1 text-sm text-[#4a6e77]">
            {zh ? "管理产品目录页与后台使用的分类" : "Manage the categories used by the storefront and admin filter."}
          </p>
        </div>
        {!editing && (
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] transition-colors duration-200"
            data-testid="new-category-btn"
          >
            <Plus size={14} /> {zh ? "新建分类" : "New Category"}
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-4 max-w-2xl" data-testid="category-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">
                Slug {editing.__isNew ? "" : (zh ? "(不可修改)" : "(immutable)")}
              </label>
              <input
                required
                className={inputCls + (editing.__isNew ? "" : " bg-[#f5f0e8] text-[#4a6e77] cursor-not-allowed")}
                value={editing.slug}
                disabled={!editing.__isNew}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                placeholder="e.g. jewelry"
                data-testid="cat-form-slug"
              />
              <div className="mt-1 text-[10px] text-[#4a6e77]">a-z, 0-9, -, _ · max 40</div>
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">{zh ? "排序" : "Sort Order"}</label>
              <input
                type="number"
                className={inputCls}
                value={editing.sort_order}
                onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                data-testid="cat-form-sort"
              />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">名称 (中)</label>
              <input required className={inputCls} value={editing.name_zh} onChange={(e) => setEditing({ ...editing, name_zh: e.target.value })} data-testid="cat-form-name-zh" />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">Name (EN)</label>
              <input required className={inputCls} value={editing.name_en} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} data-testid="cat-form-name-en" />
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm text-[#003645]">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid="cat-form-active"
                />
                {zh ? "启用 (显示于产品目录)" : "Active (visible on storefront)"}
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60"
              data-testid="cat-form-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t("common.save")}
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-[#e5ebeb] text-[#003645] hover:bg-[#e5ebeb]/40 transition-colors duration-200"
              data-testid="cat-form-cancel"
            >
              <X size={14} /> {t("common.cancel")}
            </button>
          </div>
        </form>
      ) : (
        <div className="mt-6 overflow-x-auto">
          {loading ? (
            <div className="text-[#4a6e77]">{t("common.loading")}</div>
          ) : items.length === 0 ? (
            <div className="text-[#4a6e77] py-8">
              {zh ? "暂无分类 — 请点击「新建分类」创建。" : "No categories yet — click New Category to create one."}
            </div>
          ) : (
            <table className="w-full text-sm border-collapse" data-testid="admin-categories-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Slug</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">{zh ? "名称 (中)" : "Name (ZH)"}</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">{zh ? "名称 (EN)" : "Name (EN)"}</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">{zh ? "排序" : "Order"}</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">{zh ? "状态" : "Status"}</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.slug} className="hover:bg-[#fdfbf7] transition-colors duration-150" data-testid={`admin-cat-row-${c.slug}`}>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] font-mono text-[#00868e]">{c.slug}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">{c.name_zh}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">{c.name_en}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">{c.sort_order ?? 0}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb]">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] ${c.active ? "bg-[#00868e]/10 text-[#00868e]" : "bg-red-50 text-red-700"}`}>
                        {c.active ? t("common.active") : t("common.inactive")}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb]">
                      <div className="flex gap-3">
                        <button onClick={() => openEdit(c)} className="text-[#005870] hover:text-[#00868e]" data-testid={`edit-cat-${c.slug}`}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => del(c.slug)} className="text-red-600 hover:text-red-700" data-testid={`delete-cat-${c.slug}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
